import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { STUDIO, SERVICES } from "@/data/site";
import WhatsAppIcon from "./icons/WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. We'll respond within one business day.");
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#FDFBF7] py-24 md:py-36 border-t border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-20">
        {/* Left column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="md:col-span-5"
        >
          <p className="overline">Inquire</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-4">
            Let&apos;s create
            <br />
            <span className="italic font-light">something lasting.</span>
          </h2>

          <p className="text-[#4A4A4A] mt-8 leading-relaxed max-w-md">
            Tell us about your story. We respond to every inquiry personally
            within one business day.
          </p>

          <div className="mt-12 space-y-6 text-[#1A1A1A]">
            <a
              href={`mailto:${STUDIO.email}`}
              data-testid="contact-email-link"
              className="flex items-start gap-4 group"
            >
              <Mail className="w-5 h-5 mt-0.5 text-[#C5A059] flex-shrink-0" />
              <div>
                <p className="overline mb-1">Email</p>
                <p className="font-serif-display text-lg link-underline">{STUDIO.email}</p>
              </div>
            </a>
            <a
              href={STUDIO.social.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-phone-link"
              className="flex items-start gap-4"
            >
              <Phone className="w-5 h-5 mt-0.5 text-[#C5A059] flex-shrink-0" />
              <div>
                <p className="overline mb-1">Phone · WhatsApp</p>
                <p className="font-serif-display text-lg link-underline">{STUDIO.phone}</p>
                <p className="text-xs text-[#4A4A4A] mt-1">Tap to message us on WhatsApp</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 mt-0.5 text-[#C5A059] flex-shrink-0" />
              <div>
                <p className="overline mb-1">Studio</p>
                <p className="font-serif-display text-lg">{STUDIO.address.line1}</p>
                <p className="font-serif-display text-lg">{STUDIO.address.line2}</p>
                <p className="text-sm text-[#4A4A4A] mt-2">{STUDIO.hours}</p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-5">
            <a
              href={STUDIO.social.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram"
              className="text-[#1A1A1A] hover:text-[#C5A059] transition-colors"
            >
              <Instagram size={20} />
            </a>
            <a
              href={STUDIO.social.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="text-[#1A1A1A] hover:text-[#C5A059] transition-colors"
            >
              <WhatsAppIcon size={20} />
            </a>
          </div>
        </motion.div>

        {/* Right column - form */}
        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="md:col-span-7"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
            <div>
              <label className="overline">Full name</label>
              <input
                data-testid="contact-name-input"
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="line-input"
                placeholder="Your name"
                required
              />
            </div>
            <div>
              <label className="overline">Email</label>
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="line-input"
                placeholder="you@email.com"
                required
              />
            </div>
            <div>
              <label className="overline">Phone</label>
              <input
                data-testid="contact-phone-input"
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="line-input"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="overline">Service of interest</label>
              <select
                data-testid="contact-service-select"
                value={form.service}
                onChange={(e) => update("service", e.target.value)}
                className="line-input appearance-none cursor-pointer"
              >
                <option value="">Select a service</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>
                    {s.title}
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="md:col-span-2 mt-2">
              <label className="overline">Tell us about your story</label>
              <textarea
                data-testid="contact-message-input"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="line-input resize-none"
                placeholder="Dates, location, vision..."
                required
              />
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={submitting}
              className="group inline-flex items-center gap-3 px-10 py-4 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.28em] hover:bg-[#C5A059] transition-colors duration-500 disabled:opacity-50"
            >
              {submitting ? "Sending..." : "Send Inquiry"}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <p className="text-xs text-[#4A4A4A] max-w-sm leading-relaxed">
              By submitting you agree to be contacted by lsb-studio. We never
              share your details.
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
